import { motion } from "motion/react";
import { Link } from "react-router-dom";
import { Sprout, Building2, Factory, Stethoscope, Truck, Package, Store, ShoppingBag } from "lucide-react";

export function SegmentosPage() {
  const segmentGroups = [
    {
      category: "Agroindústria",
      description: "Soluções especializadas para o agronegócio industrial",
      segments: [
        {
          icon: Sprout,
          name: "Avicultura",
          desc: "Gestão completa de granjas, matrizes, incubatórios e produção avícola",
          gradient: "from-yellow-500 to-orange-600",
          available: true
        },
        {
          icon: Factory,
          name: "Produção Industrial",
          desc: "Controle de fábricas de alimentos e rações com rastreabilidade total",
          gradient: "from-blue-500 to-indigo-600",
          available: true
        },
        {
          icon: Building2,
          name: "Frigoríficos",
          desc: "Gestão de abate, processamento e controle de qualidade SIF",
          gradient: "from-cyan-500 to-blue-600",
          available: true
        },
        {
          icon: Package,
          name: "Abatedouros",
          desc: "Controle completo de abate, rastreabilidade e conformidade",
          gradient: "from-purple-500 to-violet-600",
          available: true
        },
      ]
    },
    {
      category: "Varejo & Distribuição",
      description: "Gestão integrada para operações comerciais",
      segments: [
        {
          icon: Store,
          name: "Varejo",
          desc: "Gestão de vendas, estoque e relacionamento com clientes",
          gradient: "from-green-500 to-emerald-600",
          available: true
        },
        {
          icon: Truck,
          name: "Distribuidoras",
          desc: "Controle de distribuição, logística e gestão de pedidos",
          gradient: "from-orange-500 to-red-600",
          available: true
        },
      ]
    },
    {
      category: "Novos Segmentos",
      description: "Em desenvolvimento para atender novos mercados",
      segments: [
        {
          icon: ShoppingBag,
          name: "Farmácias",
          desc: "Gestão farmacêutica com controle de estoque e vendas",
          gradient: "from-pink-500 to-rose-600",
          available: false,
          comingSoon: true
        },
        {
          icon: Sprout,
          name: "PetShop",
          desc: "Gestão completa para pet shops e clínicas veterinárias",
          gradient: "from-amber-500 to-yellow-600",
          available: false,
          comingSoon: true
        },
        {
          icon: Stethoscope,
          name: "Clínicas e Saúde",
          desc: "Gestão inteligente para operações de saúde",
          gradient: "from-teal-500 to-cyan-600",
          available: false,
          comingSoon: true
        },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      <section className="py-24 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Soluções para <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                diversos segmentos
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expertise em operações complexas que exigem gestão profissional, rastreabilidade e alta performance
            </p>
          </motion.div>
        </div>
      </section>

      {/* Segment Groups */}
      {segmentGroups.map((group, groupIndex) => (
        <section key={groupIndex} className={`py-16 px-6 ${groupIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {group.category}
              </h2>
              <p className="text-lg text-gray-600">
                {group.description}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {group.segments.map((segment, index) => (
                <motion.div
                  key={index}
                  className={`group bg-white rounded-3xl p-8 shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 cursor-pointer overflow-hidden relative ${
                    !segment.available ? 'opacity-75' : ''
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                >
                  {segment.comingSoon && (
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1 bg-gradient-to-r from-green-500 to-blue-500 text-white text-xs font-semibold rounded-full">
                        Em breve
                      </span>
                    </div>
                  )}

                  <div className={`absolute inset-0 bg-gradient-to-br ${segment.gradient} opacity-0 group-hover:opacity-5 transition-opacity`}></div>

                  <div className={`w-16 h-16 bg-gradient-to-br ${segment.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg relative z-10`}>
                    <segment.icon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3 relative z-10">{segment.name}</h3>
                  <p className="text-gray-600 relative z-10">{segment.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Expertise Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full mb-6">
                <Sprout className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700 font-medium">Nossa vertical principal</span>
              </div>

              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Expertise em <span className="text-green-600">agroindústria</span>
              </h2>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Nossa jornada começou desenvolvendo a plataforma mais avançada do mercado para avicultura industrial.
                Essa especialização nos permitiu criar uma arquitetura capaz de lidar com operações de alta complexidade.
              </p>

              <div className="grid grid-cols-2 gap-6">
                {[
                  { value: "12M+", label: "Aves gerenciadas" },
                  { value: "2TB+", label: "Dados processados" },
                  { value: "99.9%", label: "Uptime" },
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-2xl p-12">
              <div className="space-y-6">
                {["Avicultura de Corte & Postura", "Frigoríficos & Abatedouros", "Fábricas de Ração", "Gestão Empresarial"].map((item, i) => (
                  <motion.div
                    key={i}
                    className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="font-medium text-gray-900">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-gradient-to-br from-green-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Seu segmento merece gestão profissional
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Conheça como a BusiFarm pode transformar a gestão do seu negócio
            </p>
            <Link to="/demonstracao">
              <motion.button
                className="px-8 py-4 bg-white text-green-700 rounded-xl hover:shadow-2xl transition-all font-medium"
                whileHover={{ scale: 1.05 }}
              >
                Solicitar demonstração
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
